/**
 * Generic category-card row.
 * Renders a horizontal set of selectable cards (one per option).
 * Used by AlertRuleDrawer (5 alert categories) and RoundupEditor (check-type filter).
 */
import React from 'react';
import { Box, Typography, Stack } from '@mui/material';

export interface CategoryOption<T extends string = string> {
  value: T;
  label: string;
  subtitle: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}

interface Props<T extends string = string> {
  options: CategoryOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** Optional label rendered above the card row */
  label?: string;
}

function CategoryCardPicker<T extends string = string>({
  options,
  value,
  onChange,
  label,
}: Props<T>) {
  return (
    <Box>
      {label && (
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem' }}
        >
          {label}
        </Typography>
      )}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => onChange(opt.value)}
              sx={{
                flex: '1 1 120px',
                p: 1.5,
                border: '2px solid',
                borderColor: selected ? opt.color : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                bgcolor: selected ? opt.bg : 'background.paper',
                transition: 'all .15s',
                textAlign: 'center',
                userSelect: 'none',
              }}
            >
              <Box sx={{ color: selected ? opt.color : 'text.disabled', fontSize: 26, mb: 0.5, display: 'flex', justifyContent: 'center' }}>
                {opt.icon}
              </Box>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: selected ? opt.color : 'text.primary', lineHeight: 1.2 }}
              >
                {opt.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {opt.subtitle}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default CategoryCardPicker;
